import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, Users, Briefcase, FileText, Receipt, MessageSquare,
  LogOut, ChevronRight, Building2, FileSignature, Star, FolderLock
} from "lucide-react";

const ADMIN_NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Leads / CRM", icon: MessageSquare },
  { to: "/admin/clients", label: "Clients", icon: Users },
  { to: "/admin/projects", label: "Projects", icon: Briefcase },
  { to: "/admin/floor-plans", label: "Floor Plans", icon: Building2 },
  { to: "/admin/estimates", label: "Estimates", icon: FileSignature },
  { to: "/admin/invoices", label: "Invoices", icon: Receipt },
  { to: "/admin/documents", label: "Documents", icon: FolderLock },
  { to: "/admin/testimonials", label: "Testimonials", icon: Star },
];

const CLIENT_NAV = [
  { to: "/portal", label: "My Dashboard", icon: LayoutDashboard, end: true },
  { to: "/portal/estimates", label: "Estimates", icon: FileSignature },
  { to: "/portal/invoices", label: "Invoices", icon: Receipt },
  { to: "/portal/documents", label: "My Documents", icon: FolderLock },
];

export default function DashboardLayout({ children, role = "admin", title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const NAV = role === "admin" ? ADMIN_NAV : CLIENT_NAV;

  const onLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <div className="min-h-screen flex" style={{background: 'var(--bg)'}}>
      <aside className="w-64 shrink-0 border-r border-white/5 hidden md:flex flex-col" style={{background: 'rgba(11,19,43,0.6)'}}>
        <Link to="/" className="flex items-center gap-3 px-4 h-20 border-b border-white/5">
          <img src="/brand/mark-white-256.png" alt="Coastal Bend" className="h-11 w-auto object-contain" />
          <div className="leading-tight">
            <div className="font-display font-bold text-[14px]">Coastal Bend</div>
            <div className="text-[10px] uppercase tracking-[0.22em]" style={{color:'var(--accent)'}}>
              {role === "admin" ? "Contractor HQ" : "Client Portal"}
            </div>
          </div>
        </Link>

        <nav className="flex-1 px-3 py-5 space-y-1">
          <div className="label-overline px-3 mb-2">{role === "admin" ? "Workspace" : "Account"}</div>
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`sidebar-${n.label.toLowerCase().replace(/[ /]/g, '-')}`}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
            >
              <n.icon size={16} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/5 p-3">
          <div className="px-3 py-2">
            <div className="text-[11px] uppercase tracking-wider" style={{color:'var(--text-dim)'}}>Signed in as</div>
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs truncate" style={{color:'var(--text-muted)'}}>{user?.email}</div>
          </div>
          <button onClick={onLogout} className="sidebar-link w-full" data-testid="sidebar-logout">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="border-b border-white/5 px-5 lg:px-10 h-16 flex items-center justify-between" style={{background: 'rgba(11,19,43,0.4)'}}>
          <div className="flex items-center gap-3 text-sm" style={{color:'var(--text-muted)'}}>
            <Link to="/" className="hover:text-white">Site</Link>
            <ChevronRight size={14} />
            <span className="text-white">{title || (role === "admin" ? "Dashboard" : "Portal")}</span>
          </div>
          <div className="md:hidden">
            <button onClick={onLogout} className="text-sm" style={{color:'var(--accent)'}}>Logout</button>
          </div>
        </header>

        <div className="p-5 lg:p-10">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">{title}</h1>
              {subtitle && <p className="text-sm mt-2" style={{color:'var(--text-muted)'}}>{subtitle}</p>}
            </div>
            {actions}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
